import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap, type ApiEnvelope } from '@/lib/api/client';

/**
 * A store's staff — the people with a business profile on it.
 *
 * A *user* is an account on the platform; a *business profile* is that account
 * granted access to one store, with its own set of privileges, notification
 * preferences and roles. Adding someone to a store creates the second from the
 * first; nobody is created here.
 *
 * Checked against docs/grocefy-api.swagger.json. The one thing to watch is the
 * gap between the DTO and the binding model — see `toProfileInput`.
 */

const get = <T>(url: string) => unwrap<T>(api.get<ApiEnvelope<T>>(url));

/** `UserBusinessUserProfileDto`. */
export interface StaffProfile {
  id: string;
  userId: string | null;
  imageUrl: string | null;
  email: string | null;
  completeName: string | null;
  phoneNumber: string | null;
  businessId: string | null;
  businessName: string | null;
  createdAt: string | null;
  isActive: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  hiddenFromStaff: boolean;
  canEditPayments: boolean;
  canViewAnalytics: boolean;
  canCreateMenuItems: boolean;
  canEditMenuItems: boolean;
  canEditPrice: boolean;
  canEditStore: boolean;
  canEditOrders: boolean;
  canEditLocation: boolean;
  canProvideCredit: boolean;
  canCancelOrders: boolean;
  canActivateUsers: boolean;
  canEditUsers: boolean;
  forceSms: boolean;
  getNotifications: boolean;
  getPickUpNotifications: boolean;
  getDeliveryNotifications: boolean;
  getOrderNotifications: boolean;
  getSensitiveFinanceNotifications: boolean;
  getSensitiveNotifications: boolean;
  securityPassCode: string | null;
  userLoginPin: string | null;
  roles: string[] | null;
  lastPlatformUsed: number;
}

/**
 * `BusinessUserProfileBindingModel` — every field the update accepts.
 *
 * Narrower than the DTO, and the difference matters. Four privileges the DTO
 * reports — `canEditPayments`, `canViewAnalytics`, `canActivateUsers` and
 * `forceSms` — plus `roles` are simply not in this model, so they cannot be
 * saved through it. The original showed switches for the first two anyway.
 *
 * `getLaneNotifications` runs the other way: it is in the model, not in the
 * DTO, and its booleans are not nullable, so leaving it out sets it to false.
 * The original left it out on every save.
 */
export interface StaffProfileInput {
  isActive: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  hiddenFromStaff: boolean;
  canCreateMenuItems: boolean;
  canEditMenuItems: boolean;
  canEditPrice: boolean;
  canEditStore: boolean;
  canEditLocation: boolean;
  canEditOrders: boolean;
  canCancelOrders: boolean;
  canProvideCredit: boolean;
  canEditUsers: boolean;
  getLaneNotifications: boolean;
  getOrderNotifications: boolean;
  getPickUpNotifications: boolean;
  getDeliveryNotifications: boolean;
  getNotifications: boolean;
  getSensitiveFinanceNotifications: boolean;
  getSensitiveNotifications: boolean;
  deletedAt: string | null;
}

/**
 * The row as the update wants it.
 *
 * `getLaneNotifications` has no field on the row to read, so it is carried on
 * the draft the drawer edits and passed in — a save must not silently answer
 * "false" for a setting nobody was shown.
 */
export function toProfileInput(
  profile: StaffProfile,
  getLaneNotifications: boolean
): StaffProfileInput {
  return {
    isActive: profile.isActive,
    isAdmin: profile.isAdmin,
    isSuperAdmin: profile.isSuperAdmin,
    hiddenFromStaff: profile.hiddenFromStaff,
    canCreateMenuItems: profile.canCreateMenuItems,
    canEditMenuItems: profile.canEditMenuItems,
    canEditPrice: profile.canEditPrice,
    canEditStore: profile.canEditStore,
    canEditLocation: profile.canEditLocation,
    canEditOrders: profile.canEditOrders,
    canCancelOrders: profile.canCancelOrders,
    canProvideCredit: profile.canProvideCredit,
    canEditUsers: profile.canEditUsers,
    getLaneNotifications,
    getOrderNotifications: profile.getOrderNotifications,
    getPickUpNotifications: profile.getPickUpNotifications,
    getDeliveryNotifications: profile.getDeliveryNotifications,
    getNotifications: profile.getNotifications,
    getSensitiveFinanceNotifications: profile.getSensitiveFinanceNotifications,
    getSensitiveNotifications: profile.getSensitiveNotifications,
    deletedAt: null,
  };
}

/** The privileges the update can actually save, in the order they are shown. */
export const STAFF_PRIVILEGES = [
  'canEditOrders',
  'canCancelOrders',
  'canProvideCredit',
  'canCreateMenuItems',
  'canEditMenuItems',
  'canEditPrice',
  'canEditStore',
  'canEditLocation',
  'canEditUsers',
] as const;

/** Shown, but the update model has nowhere to put them. */
export const READ_ONLY_PRIVILEGES = [
  'canEditPayments',
  'canViewAnalytics',
  'canActivateUsers',
  'forceSms',
] as const;

export const STAFF_NOTIFICATIONS = [
  'getNotifications',
  'getOrderNotifications',
  'getPickUpNotifications',
  'getDeliveryNotifications',
  'getSensitiveNotifications',
  'getSensitiveFinanceNotifications',
] as const;

// ------------------------------------------------------------------- staff

export function useStaff(businessId: string | null) {
  return useQuery({
    queryKey: ['staff', businessId],
    queryFn: () => get<{ results: StaffProfile[] }>(`businesses/${businessId}/team`),
    // Inactive people sink to the bottom rather than disappearing: the list is
    // also how somebody is brought back.
    select: (data) =>
      [...(data.results ?? [])].sort(
        (a, b) =>
          Number(b.isActive) - Number(a.isActive)
          || (a.completeName ?? '').localeCompare(b.completeName ?? '')
      ),
    enabled: !!businessId,
  });
}

function useInvalidateStaff() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['staff'] });
}

/** Keyed by the *profile* id, not the user's. */
export function useUpdateStaffProfile() {
  const invalidate = useInvalidateStaff();

  return useMutation({
    mutationFn: ({
      profileId,
      input,
    }: {
      profileId: string;
      input: StaffProfileInput;
    }) =>
      unwrap<StaffProfile>(
        api.put(`businesses/${profileId}/businessprofile`, input)
      ),
    onSuccess: invalidate,
  });
}

/** `MeUserDto`, trimmed to what the add-someone search shows. */
export interface FoundUser {
  id: string;
  completeName: string | null;
  email: string | null;
  phoneNumber: string | null;
  imageUrl: string | null;
}

export function useUserSearch(searchText: string) {
  return useQuery({
    queryKey: ['staff', 'user-search', searchText],
    queryFn: () =>
      unwrap<{ results: FoundUser[] }>(
        api.post('users/search', { searchText })
      ),
    select: (data) => data.results ?? [],
    enabled: searchText.trim().length > 0,
  });
}

/** Give an existing account a profile on this store. */
export function useAddToStore() {
  const invalidate = useInvalidateStaff();

  return useMutation({
    mutationFn: ({ userId, businessId }: { userId: string; businessId: string }) =>
      unwrap<StaffProfile>(
        api.post(`users/${userId}/businessprofile/${businessId}`, {})
      ),
    onSuccess: invalidate,
  });
}

// ------------------------------------------------------------------- codes

/**
 * A manager code, and a new one.
 *
 * Both answer the code as `data`, a bare string. Regenerating invalidates the
 * old one, which is why it asks first.
 */
export function useManagerCode() {
  return useMutation({
    mutationFn: (userId: string) =>
      get<string>(`admin/getmanagercode/${userId}`),
  });
}

export function useRegenerateManagerCode() {
  return useMutation({
    mutationFn: (userId: string) =>
      unwrap<string>(api.post(`admin/generatenewmanagercode/${userId}`, {})),
  });
}

/**
 * A one-off manager code for the store rather than for a person.
 *
 * The code comes back in the envelope's `message`, not its `data`, which is
 * why this reaches past `unwrap` for the whole envelope.
 */
export function useTemporaryManagerCode() {
  return useMutation({
    mutationFn: async (businessId: string) => {
      const response = await api.post<ApiEnvelope<unknown>>(
        `pos/tempmanagercode/${businessId}`,
        {}
      );
      return response.data;
    },
  });
}

/** Sends the login PIN to the person. Same envelope-message shape. */
export function useSendLoginPin() {
  return useMutation({
    mutationFn: async (userId: string) => {
      const response = await api.get<ApiEnvelope<unknown>>(
        `cs/users/logincode/${userId}`
      );
      return response.data;
    },
  });
}

// ------------------------------------------------------------- performance

/** What one person got through, picking and packing. */
export interface PerformanceSummary {
  orderCount: number | null;
  itemsPerOrder: number | null;
  /** Null means every item was found, so there is no rate to give. */
  itemsPerMinute: number | null;
  averageTime: number | null;
}

export function useEmployeePerformance(
  profileId: string | null,
  range: { startDate: string; endDate: string }
) {
  return useQuery({
    queryKey: ['staff', 'performance', profileId, range],
    queryFn: () =>
      unwrap<{ picking: PerformanceSummary; packing: PerformanceSummary }>(
        api.post(`analytics/v2/summary/performance/${profileId}`, range)
      ),
    enabled: !!profileId,
  });
}
